<script setup>

    import { computed, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'

    import { useClientSingleStore, useConfigStore, useViewStore } from '@/Stores'

    import { util, SERVICE_TYPES_OPTIONS, listToSelectOptions } from '@/Helpers'

    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import DatePicker from '@/Components/Forms/DatePicker.vue'
    import LwxSelect from '@/Components/Forms/LwxSelect.vue'
    import TextInput from '@/Components/Forms/TextInput.vue'
    import MultiSelect from 'primevue/multiselect'
    import Skeleton from 'primevue/skeleton'

    import { getModifiedProperties } from '@/Helpers'

    const emit = defineEmits(['client:update', 'client:cancel-update'])

    const clientSingleStore = useClientSingleStore()
    const configStore = useConfigStore()
    const viewStore = useViewStore()
    const { isEmbedded } = storeToRefs(viewStore)
    const { config } = storeToRefs(configStore)
    const { client, loading } = storeToRefs(clientSingleStore)

    const localClient = ref({
        disposition_reasons: [],
        quick_recall: null,
        service_level: null,
        service_types: [],
        number_of_adults: null,
        estimated_start_date: null,
        estimated_end_date: null,
        follow_up_date: null,
        pay_types: [],
        rate_quoted_hourly: null,
        rate_quoted_daily: null,
        ltc_insurance_company: null,
        ltc_insurance_policy_number: null,
        ltc_insurance_claim_number: null,
        marketing_source_type: null,
        marketing_source_detail: null,
        also_considering_types: [],
        also_considering_detail: null,
    })

    const hasClientData = computed(() => {
        const hasData = client.value && client.value.id
        return hasData
    })

    function formatCurrencyId(input) {
        return input.toFixed(2)
    }

    function formatCurrencyName(input) {
        return util.currency(input)
    }

    function createCurrencySelectOptions(list) {
        return listToSelectOptions(list, formatCurrencyId, formatCurrencyName)
    }

    const service_level_options = [
        {
            id: 'full_time',
            name: 'Full Time'
        },
        {
            id: 'part_time',
            name: 'Part Time'
        },
        {
            id: 'full_time_temp',
            name: 'Full Time - Temp'
        },
        {
            id: 'part_time_temp',
            name: 'Part Time - Temp'
        },
        {
            id: '24_7_hourly',
            name: '24/7 Hourly'
        },
        {
            id: '24_7_live_in',
            name: '24/7 Live-In'
        },
        {
            id: 'placement',
            name: 'Placement'
        }
    ]

    const number_of_adults_options = [
        {
            id: 1,
            name: 'Single'
        },
        {
            id: 2,
            name: 'Couple'
        }
    ]

    const pay_types_options = [
        {
            id: 'private_pay',
            name: 'Private Pay'
        },
        {
            id: 'ltc',
            name: 'LTC Insurance'
        },
        {
            id: 'other',
            name: 'Other'
        }
    ]

    const hourly_rate_options = ref(createCurrencySelectOptions([]))

    const daily_rate_options = ref(createCurrencySelectOptions([]))

    const ltc_insurance_company_options = ref(listToSelectOptions([]))

    const marketing_source_options = ref(listToSelectOptions([]))

    const also_considering_options = ref(listToSelectOptions([]))

    const clientPropsChange = ref(false)

    watch(() => client.value, (value) => {
        localClient.value = JSON.parse(JSON.stringify(value))

        if (localClient.value.id) {
            clientPropsChange.value = true
        }

        localClient.value.marketing_source_type = value.marketing_source_type ? marketing_source_options.value.find(option => option.id === value.marketing_source_type) || null : null
    }, { deep: true, immediate: true })

    watch(() => config.value, (value) => {
        if (value?.prospect_rates?.hourly) {
            hourly_rate_options.value = createCurrencySelectOptions(Array.from(value.prospect_rates.hourly))
        }
        if (value?.prospect_rates?.daily) {
            daily_rate_options.value = createCurrencySelectOptions(Array.from(value.prospect_rates.daily))
        }
        if (value?.prospect_also_considering_types) {
            also_considering_options.value = listToSelectOptions(Array.from(value.prospect_also_considering_types))
        }
        if (value?.client_marketing_source_types) {
            marketing_source_options.value = listToSelectOptions(Array.from(value.client_marketing_source_types))
        }
        if (value?.ltc_insurance_companies) {
            ltc_insurance_company_options.value = listToSelectOptions(Array.from(value.ltc_insurance_companies))
        }
    }, { deep: true, immediate: true })


    function extractClient(proxyClient) {

        return {
            ...proxyClient,
        };
    }

    watch(localClient, (newValue, oldValue) => {
        if (oldValue === undefined) {
            clientPropsChange.value = false;
            return;
        }

        if (oldValue.id === newValue.id) {
            if(clientPropsChange.value == false) {

                // console.log(JSON.stringify(extractClient(newValue)))
                // console.log(JSON.stringify(client.value))

                const diff = getModifiedProperties(
                    JSON.stringify(extractClient(newValue)),
                    JSON.stringify(client.value),
                    [
                        'disposition_reasons',
                        'quick_recall',
                        'service_level',
                        'service_types',
                        'number_of_adults',
                        'estimated_start_date',
                        'estimated_end_date',
                        'follow_up_date',
                        'pay_types',
                        'rate_quoted_hourly',
                        'rate_quoted_daily',
                        'ltc_insurance_company',
                        'ltc_insurance_policy_number',
                        'ltc_insurance_claim_number',
                        {
                            key: 'marketing_source_type',
                            diff: (newObj, oldObj) => {
                                return newObj.id !== oldObj ? newObj : null
                            }
                        },
                        'marketing_source_detail',
                        'also_considering_types',
                        'also_considering_detail'
                    ]
                )
                handleChange(diff)
                return;
            }
        }
        clientPropsChange.value = false
    }, { immediate: true, deep: true })

    const handleChange = (data) => {
        if (Object.keys(data).length > 0) {
            emit('client:update', data);
        } else {
            emit('client:cancel-update', client.value.id);
        }
    }

    const minEndDate = computed(() => localClient.value.estimated_start_date || null)
    const maxStartDate = computed(() => localClient.value.estimated_end_date || null)

    const coffeeBasedEditProfileUrl = import.meta.env.VITE_COFFEE_BASEURL + '/staff/salesmanager-offlineagency/edit-profile/pid';
</script>

<template>

    <div
        id="client_basic_info"
        class="p-6 pb-8"
    >
        <div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-y-2 gap-x-6">

            <div class="col-span-full">
                <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Service</span>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-servicetypes"
                    value="Service Types"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <MultiSelect
                        v-else
                        id="client-basicinfo-servicetypes"
                        v-model="localClient.service_types"
                        :options="SERVICE_TYPES_OPTIONS"
                        optionLabel="name"
                        optionValue="id"
                        :maxSelectedLabels="3"
                        class="w-full"
                        display="chip"
                        :pt="{
                            root: { class: 'text-xs h-[34px]' },
                            overlay: { class: 'text-sm !z-modal' },
                            header: { class: 'hidden'}
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-servicelevel"
                    value="Service Level"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <Select
                        v-else
                        id="client-basicinfo-servicelevel"
                        v-model="localClient.service_level"
                        :options="service_level_options"
                        optionLabel="name"
                        optionValue="id"
                        class="w-full"
                        checkmark
                        :highlightOnSelect="true"
                        :pt="{
                            root: 'text-xs h-[34px]',
                            label: 'text-xs',
                            overlay: 'text-xs',
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-2">
                <InputLabel
                    for="client-basicinfo-numberofadults"
                    value="Number of Adults"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <Select
                        v-else
                        id="client-basicinfo-numberofadults"
                        v-model="localClient.number_of_adults"
                        :options="number_of_adults_options"
                        optionLabel="name"
                        optionValue="id"
                        class="w-full"
                        checkmark
                        :highlightOnSelect="true"
                        :pt="{
                            root: 'text-xs h-[34px]',
                            label: 'text-xs',
                            overlay: 'text-xs',
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-2">
                <InputLabel
                    for="client-basicinfo-estimatedstartdate"
                    value="Est. Start Date"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <DatePicker
                        v-else
                        v-model="localClient.estimated_start_date"
                        id="client-basicinfo-estimatedstartdate"
                        text-input
                        min-date="1900-01-01"
                        :max-date="maxStartDate"
                        :clearable="true"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-2">
                <InputLabel
                    for="client-basicinfo-estimatedenddate"
                    value="Est. End Date"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <DatePicker
                        v-else
                        v-model="localClient.estimated_end_date"
                        id="client-basicinfo-estimatedenddate"
                        text-input
                        :min-date="minEndDate"
                        :clearable="true"
                    />
                </InputLabel>
            </div>


            <div class="col-span-full md:pt-4">
                <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Funding &amp; Pricing</span>
            </div>

            <div class="sm:col-span-6">
                <InputLabel
                    for="client-basicinfo-paytypes"
                    value="Pay Types"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <MultiSelect
                        v-else
                        id="client-basicinfo-paytypes"
                        v-model="localClient.pay_types"
                        :options="pay_types_options"
                        optionLabel="name"
                        optionValue="id"
                        :maxSelectedLabels="3"
                        class="w-full"
                        display="chip"
                        :pt="{
                            root: { class: 'text-xs h-[34px]' },
                            overlay: { class: 'text-sm !z-modal' },
                            header: { class: 'hidden'}
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-hourlyratequoted"
                    value="Hourly Rate Quoted"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <Select
                        v-else
                        id="client-basicinfo-hourlyratequoted"
                        v-model="localClient.rate_quoted_hourly"
                        :options="hourly_rate_options"
                        optionLabel="name"
                        optionValue="id"
                        class="w-full"
                        checkmark
                        show-clear
                        :highlightOnSelect="true"
                        :pt="{
                            root: 'text-xs h-[34px]',
                            label: 'text-xs',
                            overlay: 'text-xs !z-modal',
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-dailyratequoted"
                    value="Daily Rate Quoted"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <Select
                        v-else
                        id="client-basicinfo-dailyratequoted"
                        v-model="localClient.rate_quoted_daily"
                        :options="daily_rate_options"
                        optionLabel="name"
                        optionValue="id"
                        class="w-full"
                        checkmark
                        show-clear
                        :highlightOnSelect="true"
                        :pt="{
                            root: 'text-xs h-[34px]',
                            label: 'text-xs',
                            overlay: 'text-xs !z-modal',
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-6">
                <InputLabel
                    for="client-basicinfo-ltcoinsurancecompany"
                    value="LTC Insurance Company"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <Select
                        v-else
                        id="client-basicinfo-ltcinsurancecompany"
                        v-model="localClient.ltc_insurance_company"
                        :options="ltc_insurance_company_options"
                        optionLabel="name"
                        optionValue="id"
                        class="w-full"
                        checkmark
                        :highlightOnSelect="true"
                        :pt="{
                            root: 'text-xs h-[34px]',
                            label: 'text-xs',
                            overlay: 'text-xs !z-modal',
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-ltcinsurancepolicynumber"
                    value="Policy Number"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <TextInput
                        v-else
                        id="client-basicinfo-ltcinsurancepolicynumber"
                        v-model="localClient.ltc_insurance_policy_number"
                        type="text"
                        class="mt-1 block w-full"
                        autocomplete="off"
                    />
                </InputLabel>
            </div>

                <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-ltcinsuranceclaimnumber"
                    value="Claim/Intake Number"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <TextInput
                        v-else
                        id="client-basicinfo-ltcinsuranceclaimnumber"
                        v-model="localClient.ltc_insurance_claim_number"
                        type="text"
                        class="mt-1 block w-full"
                        autocomplete="off"
                    />
                </InputLabel>
            </div>

            <div class="col-span-full md:pt-4">
                <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Marketing</span>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-marketingsourcetype"
                    value="Source"
                    class="text-xs text-stone-400 mb-3"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <LwxSelect
                        v-else
                        id="client-basicinfo-marketingsourcetype"
                        :disabled="true"
                        v-model="localClient.marketing_source_type"
                        :options="marketing_source_options"
                    />
                </InputLabel>

                <div class="text-xs ml-0.5"><a target="_blank" :href="`${coffeeBasedEditProfileUrl}/${client.coffee_user_profile_id}#client_hear_us`" class="hover:underline">Edit on Coffee</a></div>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-marketingsourcedetail"
                    value="Detail"
                    class="text-xs text-stone-400"
                    :disabled="localClient.marketing_source_type == null"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <TextInput
                        v-else
                        id="client-basicinfo-marketingsourcedetail"
                        v-model="localClient.marketing_source_detail"
                        type="text"
                        class="mt-1 block w-full disabled:opacity-50"
                        :disabled="true"
                        autocomplete="off"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-alsoconsideringtypes"
                    value="Also Considering"
                    class="text-xs text-stone-400"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <MultiSelect
                        v-else
                        id="client-basicinfo-alsoconsideringtypes"
                        v-model="localClient.also_considering_types"
                        :options="also_considering_options"
                        optionLabel="name"
                        optionValue="id"
                        :maxSelectedLabels="3"
                        class="w-full"
                        display="chip"
                        :pt="{
                            root: { class: 'text-xs h-[34px]' },
                            overlay: { class: 'text-sm !z-modal' },
                            header: { class: 'hidden'}
                        }"
                    />
                </InputLabel>
            </div>

            <div class="sm:col-span-3">
                <InputLabel
                    for="client-basicinfo-alsoconsideringdetail"
                    value="Detail"
                    class="text-xs text-stone-400"
                    :disabled="localClient.also_considering_types == null || localClient.also_considering_types.length === 0"
                >
                    <Skeleton
                        v-if="!hasClientData && loading"
                        height="34px"
                        class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                    />
                    <TextInput
                        v-else
                        id="client-basicinfo-alsoconsideringdetail"
                        v-model="localClient.also_considering_detail"
                        type="text"
                        class="mt-1 block w-full disabled:opacity-50"
                        autocomplete="off"
                        :disabled="localClient.also_considering_types == null || localClient.also_considering_types.length === 0"
                    />
                </InputLabel>
            </div>

        </div>

    </div>

</template>
